import { useEffect, useState } from "react";
import { X, ShoppingCart } from "lucide-react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../constants";

/**
 * Convert any YouTube URL into its /embed/ equivalent so it plays inline in an iframe.
 * Returns the original URL if it's not a recognizable YouTube link.
 */
export const toYoutubeEmbed = (url) => {
  if (!url) return "";
  let m = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
  m = url.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
  m = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
  m = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
  return url;
};

const isYoutube = (url) => /(?:youtube\.com|youtu\.be)/i.test(url || "");

/**
 * Modal-based video player. Renders a YouTube iframe for YouTube URLs, otherwise a native <video>.
 *
 * Props:
 *   open               — boolean
 *   onClose            — () => void
 *   url                — string (any YouTube URL or direct .mp4 URL)
 *   title              — string (shown above the player)
 *   relatedProductIds  — string[] (optional list of product_ids to upsell at the bottom)
 */
const VideoPlayerModal = ({ open, onClose, url, title, relatedProductIds }) => {
  const [relatedProducts, setRelatedProducts] = useState([]);

  // Lock scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Fetch related products when modal opens with IDs
  useEffect(() => {
    if (!open || !relatedProductIds || relatedProductIds.length === 0) {
      setRelatedProducts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          relatedProductIds.slice(0, 4).map((pid) =>
            axios.get(`${API}/products/${pid}`).then((r) => r.data).catch(() => null)
          )
        );
        if (!cancelled) {
          setRelatedProducts(results.filter(Boolean));
        }
      } catch (e) {
        if (!cancelled) setRelatedProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, relatedProductIds]);

  if (!open) return null;

  const yt = isYoutube(url);
  const playableSrc = yt ? toYoutubeEmbed(url) : url;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="video-player-modal"
    >
      <div
        className="relative w-full max-w-4xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-gray-300 hover:text-white p-2 rounded transition-colors flex items-center gap-1 text-sm"
          aria-label="Close video"
          data-testid="video-player-close"
        >
          <X className="w-5 h-5" /> Close
        </button>
        {title && (
          <p className="text-white font-heading text-xl mb-3 uppercase tracking-wide" data-testid="video-player-title">{title}</p>
        )}
        <div className="relative w-full bg-black rounded-sm overflow-hidden" style={{ paddingBottom: "56.25%" }}>
          {yt ? (
            <iframe
              src={playableSrc}
              title={title || "Video"}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="video-player-iframe"
            />
          ) : (
            <video
              src={playableSrc}
              controls
              autoPlay
              className="absolute inset-0 w-full h-full object-contain bg-black"
              data-testid="video-player-html5"
            >
              Sorry, your browser doesn't support embedded video.
            </video>
          )}
        </div>

        {/* Upsell CTA — products used in this video */}
        {relatedProducts.length > 0 && (
          <div
            className="mt-4 bg-gradient-to-r from-[#D62828]/20 via-[#1a1a1a] to-[#1a1a1a] border border-[#D62828]/40 rounded-sm p-4"
            data-testid="video-related-products"
          >
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-5 h-5 text-[#D62828]" />
              <p className="font-heading text-white uppercase tracking-wide text-sm">
                Get the products used in this video
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {relatedProducts.map((p) => (
                <Link
                  key={p.product_id}
                  to={`/products/${p.product_id}`}
                  onClick={onClose}
                  className="bg-black/50 border border-white/10 rounded-sm overflow-hidden hover:border-[#D62828] transition-colors group"
                  data-testid={`video-related-product-${p.product_id}`}
                >
                  {p.image_url && (
                    <div className="aspect-square bg-white/5 overflow-hidden">
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-white text-xs leading-tight line-clamp-2 mb-1">{p.name}</p>
                    <p className="text-[#D62828] font-bold text-sm">${p.online_price?.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayerModal;
